import appSettings from 'wanderu-config';

/**
 * Shared attributes for the cookies ui-react writes through universal-cookie,
 * so the next PCI ASV scan finding (see wanderu/ui-react#1173) is one edit
 * here rather than a hunt across call sites.
 *
 * - `secure` is env-gated rather than hardcoded: Safari and Playwright over
 *   plain `http://localhost` silently drop `Secure` cookies, which would break
 *   local dev and the e2e suite. Every deployed environment is https.
 * - `sameSite: 'lax'` is the modern browser default made explicit so the scan
 *   sees it on the header.
 * - `path: '/'` so a cookie set from a deep route is visible site-wide.
 * - `maxAge` is one year, in seconds (universal-cookie's unit). Browsers cap
 *   cookie lifetime at 400 days, so anything longer is silently clamped.
 *
 * `httpOnly` is deliberately absent: every cookie this module covers is read
 * by client-side script (Amplitude, Datadog RUM, user preferences), so the
 * scan's HttpOnly finding is disputed with the vendor rather than fixed here.
 *
 * Use this with `cookies.set` on both sides of the render. In the browser,
 * universal-cookie serializes it directly. On the server, both Express entries
 * mount `universal-cookie-express`, whose change listener forwards every
 * `cookies.set` to `res.cookie` and converts `maxAge` to the milliseconds
 * Express expects, so there is no need to call `res.cookie` by hand.
 */
const COOKIE_MAX_AGE_ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

interface SharedCookieOptions {
    maxAge: number;
    path: '/';
    sameSite: 'lax';
    secure: boolean;
}

function getCookieOptions(): SharedCookieOptions {
    return {
        maxAge: COOKIE_MAX_AGE_ONE_YEAR_SECONDS,
        path: '/',
        sameSite: 'lax',
        secure: !appSettings?.env?.isLocal,
    };
}

export type { SharedCookieOptions };
export { getCookieOptions };
